const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Indicates whether the specified pattern finds a match in the specified
 * input string, using the case-insensitive methods in this order:
 * 1) exact match
 * 2) entire input matches the specified regex pattern
 * @param isRegex If true, treats pattern as a dynamic regex without escaping; if false, escapes special characters.
 */
export function isFullPatternMatch(input: string, pattern: string, isRegex: boolean = false): boolean {
  if (!input || !pattern) {
    return false;
  }

  if (input.toLowerCase() === pattern.toLowerCase()) {
    return true;
  }

  if (isRegex) {
    try {
      const regex = new RegExp(pattern, "i");
      return regex.test(input);
    } catch {
      return false;
    }
  }

  const escapedPattern = escapeRegExp(pattern);
  const regexFullPattern = `^${escapedPattern}$`;
  const regex = new RegExp(regexFullPattern, "i");
  return regex.test(input);
}

/**
 * Indicates whether the specified pattern finds a match in the specified
 * input string, using the case-insensitive methods in this order:
 * 1) exact match
 * 2) input starts with the specified pattern
 * 3) input starts with a matching specified regex pattern
 * Note: A trailing ellipsis "..." denotes a basic "starts with" evaluation
 * @param isRegex If true, treats pattern as a dynamic regex without escaping; if false, escapes special characters.
 */
export function isPartialPatternMatch(input: string, pattern: string, isRegex: boolean = false): boolean {
  if (!input || !pattern) {
    return false;
  }

  if (input.toLowerCase() === pattern.toLowerCase()) {
    return true;
  }

  let isRegexAllowed = true;
  const ellipses = ["...", "…"];
  let normalizedPattern = pattern;
  for (const ellipsis of ellipses) {
    if (pattern.toLowerCase().endsWith(ellipsis.toLowerCase())) {
      isRegexAllowed = false;
      normalizedPattern = pattern.substring(0, pattern.length - ellipsis.length);
      break;
    }
  }

  if (input.toLowerCase().startsWith(normalizedPattern.toLowerCase())) {
    return true;
  }

  if (isRegexAllowed && isRegex) {
    try {
      const regexStartsWithPattern = `^${normalizedPattern}`;
      const regex = new RegExp(regexStartsWithPattern, "i");
      return regex.test(input);
    } catch {
      return false;
    }
  }

  if (isRegexAllowed) {
    const escapedPattern = escapeRegExp(normalizedPattern);
    const regexStartsWithPattern = `^${escapedPattern}`;
    const regex = new RegExp(regexStartsWithPattern, "i");
    return regex.test(input);
  }

  return false;
}
